import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import SecretsManager from "@/components/SecretsManager";
import { Cpu, Database, KeyRound, Eye, RotateCcw, CheckCircle2 } from "lucide-react";

const money = (m) => `GBP ${(((m || 0) / 100)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="k-container" data-testid={testid} style={{ padding: 20, marginBottom: 16 }}>
      <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}>
        {Icon && <Icon size={16} aria-hidden />} {title}
      </h3>
      {children}
    </div>
  );
}

export default function HostingPanel({ projectId }) {
  const confirm = useConfirm();
  const [tiers, setTiers] = useState(null);
  const [deps, setDeps] = useState([]);
  const [dep, setDep] = useState(null);
  const [dbView, setDbView] = useState(null);
  const [revealedUrl, setRevealedUrl] = useState("");

  const loadDeps = useCallback(() => {
    api.get(`/hosting/${projectId}/status`).then((r) => {
      const d = r.data.deployments || [];
      setDeps(d); setDep((cur) => cur ? d.find((x) => x.id === cur.id) || d[0] : d[0]);
    }).catch(() => {});
  }, [projectId]);

  useEffect(() => { api.get("/hosting/tiers").then((r) => setTiers(r.data)).catch(() => {}); }, []);
  useEffect(loadDeps, [loadDeps]);

  const loadDepDetails = useCallback((id) => {
    if (!id) return;
    api.get(`/hosting/${id}/database`).then((r) => setDbView(r.data)).catch(() => {});
    setRevealedUrl("");
  }, []);
  useEffect(() => { if (dep?.id) loadDepDetails(dep.id); }, [dep, loadDepDetails]);

  const changeTier = async (tid) => {
    try { const { data } = await api.post(`/hosting/${dep.id}/tier`, { tier_id: tid }); toast.success(data.note); loadDeps(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleDb = async (enabled, yearly) => {
    try { const { data } = await api.post(`/hosting/${dep.id}/database-tier`, { enabled, yearly }); toast.success(data.note); loadDeps(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revealUrl = async () => {
    try { const { data } = await api.post(`/hosting/${dep.id}/database/reveal`); setRevealedUrl(data.connection_url); toast.info("Reveal logged to the audit trail"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rotateUrl = async () => {
    const ok = await confirm({ title: "Rotate the connection string?", body: "A new connection string is generated and the old one is invalidated immediately. Update your app before it disconnects.", confirmLabel: "Rotate" });
    if (!ok) return;
    try { const { data } = await api.post(`/hosting/${dep.id}/database/rotate`); toast.success(data.note); setRevealedUrl(""); loadDepDetails(dep.id); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!deps.length) {
    return <Card title="Hosting & data" icon={Cpu} testid="hosting-panel-empty">
      <p className="k-muted" style={{ fontSize: 13 }}>No hosted deployment for this project yet. Provision one from the <a className="k-link" href="/hosting">hosting console</a> to choose a compute tier, add a dedicated database, and manage secrets.</p>
    </Card>;
  }

  const curTier = dep?.compute_tier || "starter";
  const dbt = tiers?.database_tier;

  return (
    <div data-testid="hosting-panel">
      {deps.length > 1 && (
        <select className="k-control" data-testid="hosting-panel-dep" style={{ maxWidth: 320, marginBottom: 14 }}
          value={dep?.id} onChange={(e) => setDep(deps.find((d) => d.id === e.target.value))}>
          {deps.map((d) => <option key={d.id} value={d.id}>{d.subdomain || d.id}</option>)}
        </select>
      )}

      <Card title="Compute tier" icon={Cpu} testid="compute-tier-card">
        {tiers?.caveat && <p className="k-muted" style={{ fontSize: 12, marginTop: 0 }}>{tiers.caveat}</p>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          {(tiers?.compute_tiers || []).map((t) => {
            const active = t.id === curTier;
            return (
              <div key={t.id} data-testid={`tier-${t.id}`} className="k-container" style={{ padding: 12, border: active ? "1px solid var(--accent-primary)" : undefined }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <strong style={{ fontSize: 14 }}>{t.name}</strong>
                  {active && <CheckCircle2 size={14} className="k-tag-green" aria-label="current" />}
                </div>
                <div className="k-muted" style={{ fontSize: 12, margin: "4px 0" }}>{t.vcpu} vCPU · {t.ram_mb} MB RAM</div>
                <div style={{ fontSize: 13 }}>{t.price_minor ? `${money(t.price_minor)}/mo` : "Free"}</div>
                {!active && <button className="k-btn k-btn-ghost" data-testid={`tier-select-${t.id}`} onClick={() => changeTier(t.id)} style={{ marginTop: 8, width: "100%" }}>Choose</button>}
              </div>
            );
          })}
        </div>
        {dep?.pending_compute_tier && <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>Scheduled change to <strong>{dep.pending_compute_tier}</strong> takes effect at your next billing cycle.</p>}
      </Card>

      {dbt && (
        <Card title="Dedicated database tier" icon={Database} testid="database-tier-card">
          <p className="k-muted" style={{ fontSize: 13, marginTop: 0 }}>{dbt.vcpu} vCPU · {dbt.ram_mb} MB RAM · {dbt.storage_gb} GB · {money(dbt.price_minor)}/mo or {money(dbt.price_minor_yearly)}/yr. {dbt.backup_note}</p>
          {dep?.database_tier_enabled ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="k-tag-green" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13 }}><CheckCircle2 size={14} /> Enabled ({dep.database_tier_period || "month"})</span>
              <button className="k-btn k-btn-ghost" data-testid="db-tier-disable" onClick={() => toggleDb(false)}>Remove</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="k-btn k-btn-primary" data-testid="db-tier-monthly" onClick={() => toggleDb(true, false)}>Add monthly</button>
              <button className="k-btn k-btn-ghost" data-testid="db-tier-yearly" onClick={() => toggleDb(true, true)}>Add yearly</button>
            </div>
          )}
        </Card>
      )}

      <Card title={`Database — ${dbView?.app_name || "connection"}`} icon={Database} testid="database-card">
        <p className="k-muted" style={{ fontSize: 12, marginTop: 0 }}>{dbView?.note}</p>
        <div className="mono" data-testid="db-url" style={{ fontSize: 12, wordBreak: "break-all", padding: 10, background: "var(--canvas)", borderRadius: 6 }}>
          {revealedUrl || dbView?.masked_url || "…"}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="k-btn k-btn-ghost" data-testid="db-reveal" onClick={revealUrl}><Eye size={14} /> Reveal</button>
          <button className="k-btn k-btn-ghost" data-testid="db-rotate" onClick={rotateUrl}><RotateCcw size={14} /> Rotate credentials</button>
        </div>
      </Card>

      <Card title="Secret keys" icon={KeyRound} testid="secrets-card">
        <SecretsManager deploymentId={dep?.id} compact />
      </Card>
    </div>
  );
}
